import {
  AlarmClock,
  AlertTriangle,
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  BadgeCheck,
  Ban,
  Boxes,
  Briefcase,
  CircleCheck,
  ClipboardList,
  Clock,
  FileEdit,
  Hourglass,
  KeyRound,
  Layers,
  LayoutDashboard,
  Loader2,
  Package,
  PackageCheck,
  Percent,
  Radio,
  ShoppingCart,
  TrendingUp,
  Truck,
  UserCheck,
  UserPlus,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ConditionDonutChart from '../components/charts/ConditionDonutChart';
import ShipmentsAreaChart from '../components/charts/ShipmentsAreaChart';
import SlaRadialGauge from '../components/charts/SlaRadialGauge';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  assetCategoryStatsSchema,
  assetInstanceStatisticsSchema,
  assetTemplateStatsSchema,
  dashboardTrendsDataSchema,
  jobStatsSchema,
  orderStatsSchema,
  qrCodeStatsSchema,
  quoteStatsSchema,
  shipmentStatsSchema,
  supplierStatsSchema,
  taskStatsSchema,
  type AssetCategoryStats,
  type AssetInstanceStatistics,
  type AssetTemplateStats,
  type QrCodeStats,
  type SupplierStats,
} from '../schemas/dashboard';
import { formatCurrency } from '../types/quote';
import type { JobStats } from '../types/job';
import type { OrderStats } from '../types/order';
import type { QuoteStats } from '../types/quote';
import type { ShipmentStats } from '../types/shipment';
import type { TaskStats } from '../types/task';
import {
  activeJobs,
  instanceStatusDonut,
  openOrders,
  openTasks,
  orderStatsToDonut,
} from './dashboard/dashboardHelpers';

type User = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
};

type TrendsData = {
  from: string;
  to: string;
  series: {
    orders: { date: string; count: number }[];
    jobs: { date: string; count: number }[];
    tasks: { date: string; count: number }[];
    shipments: { date: string; count: number }[];
    quotes: { date: string; count: number }[];
    asset_instances: { date: string; count: number }[];
  };
};

type StatsState = {
  orders: OrderStats | null;
  jobs: JobStats | null;
  tasks: TaskStats | null;
  quotes: QuoteStats | null;
  shipments: ShipmentStats | null;
  qr: QrCodeStats | null;
  assetInstances: AssetInstanceStatistics | null;
  assetTemplates: AssetTemplateStats | null;
  assetCategories: AssetCategoryStats | null;
  suppliers: SupplierStats | null;
};

const emptyStats = (): StatsState => ({
  orders: null,
  jobs: null,
  tasks: null,
  quotes: null,
  shipments: null,
  qr: null,
  assetInstances: null,
  assetTemplates: null,
  assetCategories: null,
  suppliers: null,
});

const statTileClass =
  'flex min-h-[5.25rem] flex-col justify-between rounded-lg border border-border/80 bg-card p-4 text-left shadow-sm transition-colors';

function StatTile({
  label,
  value,
  loading,
  error,
  onClick,
  icon: Icon,
}: {
  label: string;
  value: ReactNode;
  loading: boolean;
  error?: boolean;
  onClick?: () => void;
  icon?: LucideIcon;
}) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {loading ? (
            <div className="h-8 w-14 animate-pulse rounded bg-muted" />
          ) : error ? (
            <span className="text-sm text-destructive">—</span>
          ) : (
            <span className="text-2xl font-bold tabular-nums tracking-tight text-foreground">{value}</span>
          )}
        </div>
        {Icon && !loading && <Icon className="h-5 w-5 shrink-0 text-primary/55" aria-hidden />}
      </div>
      <span className="mt-1 block text-xs font-medium leading-tight text-muted-foreground">{label}</span>
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${statTileClass} hover:border-primary/40 hover:bg-accent/30`}>
        {body}
      </button>
    );
  }
  return <div className={statTileClass}>{body}</div>;
}

function KpiCard({
  title,
  value,
  onClick,
  loading,
  error,
  icon: Icon,
}: {
  title: string;
  value: string | number;
  onClick?: () => void;
  loading: boolean;
  error?: boolean;
  icon?: LucideIcon;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`w-full text-left p-6 rounded-lg shadow-sm border border-transparent transition-all ${
        onClick ? 'cursor-pointer hover:border-primary bg-card' : 'bg-card'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {loading ? (
            <div className="h-8 w-24 animate-pulse rounded bg-muted" />
          ) : error ? (
            <div className="text-sm text-destructive">Failed to load</div>
          ) : (
            <div className="text-2xl font-bold text-primary">{value}</div>
          )}
        </div>
        {Icon && !loading && <Icon className="h-5 w-5 shrink-0 text-primary/55" aria-hidden />}
      </div>
      <div className="text-sm text-muted-foreground mt-1">{title}</div>
    </button>
  );
}

function ChartCard({
  title,
  children,
  loading,
  error,
  ariaLabel,
}: {
  title: string;
  children: ReactNode;
  loading: boolean;
  error?: boolean;
  ariaLabel?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-64 animate-pulse rounded-md bg-muted" aria-hidden />
        ) : error ? (
          <p className="text-sm text-muted-foreground">Chart unavailable</p>
        ) : (
          <div aria-label={ariaLabel}>{children}</div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [stats, setStats] = useState<StatsState>(emptyStats);
  const [statsFailed, setStatsFailed] = useState<Partial<Record<keyof StatsState, boolean>>>({});
  const [trends, setTrends] = useState<TrendsData | null>(null);
  const [trendsFailed, setTrendsFailed] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (!token || !savedUser) {
      navigate('/login');
      return;
    }
    try {
      setUser(JSON.parse(savedUser));
    } catch {
      navigate('/login');
      return;
    }
    setAuthLoading(false);
  }, [navigate]);

  const loadData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setDataLoading(true);
    setStatsFailed({});
    setTrendsFailed(false);

    const headers = { Authorization: `Bearer ${token}` };

    const trendsRes = await fetch('/api/dashboard/trends?days=30', { headers });
    const trendsJson = await trendsRes.json().catch(() => null);
    if (trendsJson?.success && trendsJson.data) {
      const parsed = dashboardTrendsDataSchema.safeParse(trendsJson.data);
      if (parsed.success) setTrends(parsed.data);
      else {
        console.error(parsed.error);
        toast.error('Invalid dashboard trends response');
        setTrendsFailed(true);
      }
    } else {
      toast.error('Could not load dashboard trends');
      setTrendsFailed(true);
    }

    const entries: {
      key: keyof StatsState;
      url: string;
      schema: { safeParse: (data: unknown) => { success: boolean; data?: unknown } };
    }[] = [
      { key: 'orders', url: '/api/orders/stats', schema: orderStatsSchema },
      { key: 'jobs', url: '/api/jobs/stats', schema: jobStatsSchema },
      { key: 'tasks', url: '/api/tasks/stats', schema: taskStatsSchema },
      { key: 'quotes', url: '/api/quotes/stats', schema: quoteStatsSchema },
      { key: 'shipments', url: '/api/shipments/stats', schema: shipmentStatsSchema },
      { key: 'qr', url: '/api/qr-codes/stats', schema: qrCodeStatsSchema },
      { key: 'assetInstances', url: '/api/asset-instances/statistics', schema: assetInstanceStatisticsSchema },
      { key: 'assetTemplates', url: '/api/asset-templates/stats', schema: assetTemplateStatsSchema },
      { key: 'assetCategories', url: '/api/asset-categories/stats', schema: assetCategoryStatsSchema },
      { key: 'suppliers', url: '/api/suppliers/stats', schema: supplierStatsSchema },
    ];

    const settled = await Promise.allSettled(
      entries.map(e => fetch(e.url, { headers }).then(r => r.json()))
    );

    const next = emptyStats();
    const failed: Partial<Record<keyof StatsState, boolean>> = {};

    settled.forEach((result, i) => {
      const { key, schema } = entries[i];
      if (result.status === 'rejected') {
        failed[key] = true;
        return;
      }
      const json = result.value;
      if (!json?.success) {
        failed[key] = true;
        return;
      }
      const parsed = schema.safeParse(json.data);
      if (!parsed.success) {
        console.error(key, parsed);
        failed[key] = true;
        return;
      }
      Object.assign(next, { [key]: parsed.data } as Partial<StatsState>);
    });

    setStats(next);
    setStatsFailed(failed);
    setDataLoading(false);
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    loadData();
  }, [authLoading, user, loadData]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-xl font-heading text-primary">Loading Dashboard...</h2>
        </div>
      </div>
    );
  }

  const o = stats.orders;
  const j = stats.jobs;
  const t = stats.tasks;
  const inst = stats.assetInstances;
  const overdueSum =
    (o?.overdue ?? 0) + (j?.overdue ?? 0) + (t?.overdue ?? 0);

  const trendRows = trends?.series;

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="dashboard" />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-heading font-bold text-primary">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back, {user.first_name}!</p>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-6 flex h-auto w-full flex-wrap items-center justify-start gap-1 p-1">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4 shrink-0" aria-hidden />
              Overview
            </TabsTrigger>
            <TabsTrigger value="operations" className="flex items-center gap-2">
              <Truck className="h-4 w-4 shrink-0" aria-hidden />
              Operations
            </TabsTrigger>
            <TabsTrigger value="inventory" className="flex items-center gap-2">
              <Package className="h-4 w-4 shrink-0" aria-hidden />
              Inventory
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 shrink-0" aria-hidden />
              Pipeline
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard
                title="Open orders"
                value={o ? openOrders(o) : '—'}
                loading={dataLoading}
                error={statsFailed.orders}
                onClick={() => navigate('/orders')}
                icon={ShoppingCart}
              />
              <KpiCard
                title="Active jobs"
                value={j ? activeJobs(j) : '—'}
                loading={dataLoading}
                error={statsFailed.jobs}
                onClick={() => navigate('/jobs')}
                icon={Briefcase}
              />
              <KpiCard
                title="Open tasks"
                value={t ? openTasks(t) : '—'}
                loading={dataLoading}
                error={statsFailed.tasks}
                onClick={() => navigate('/tasks')}
                icon={ClipboardList}
              />
              <KpiCard
                title="Utilization"
                value={inst ? `${inst.utilization_rate}%` : '—'}
                loading={dataLoading}
                error={statsFailed.assetInstances}
                onClick={() => navigate('/assets')}
                icon={Percent}
              />
            </div>

            <Card>
              <CardContent className="pt-6">
                {dataLoading ? (
                  <div className="h-6 w-full max-w-md animate-pulse rounded bg-muted" />
                ) : overdueSum === 0 ? (
                  <p className="text-sm text-muted-foreground">No overdue items in tracked queues.</p>
                ) : (
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="font-medium text-foreground">Attention — overdue:</span>
                    <span>Orders {o?.overdue ?? '—'}</span>
                    <span>Jobs {j?.overdue ?? '—'}</span>
                    <span>Tasks {t?.overdue ?? '—'}</span>
                    <button
                      type="button"
                      className="text-primary underline"
                      onClick={() => navigate('/orders')}
                    >
                      Queues
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard
                title="New orders per day"
                loading={dataLoading}
                error={trendsFailed || !trendRows}
                ariaLabel="Area chart of new orders per day"
              >
                {trendRows && (
                  <ShipmentsAreaChart
                    data={trendRows.orders.map(d => ({ date: d.date, count: d.count }))}
                    xDataKey="date"
                    series={[
                      { key: 'count', name: 'Orders', stroke: '#0ea5e9', fill: '#0ea5e933' },
                    ]}
                  />
                )}
              </ChartCard>
              <ChartCard
                title="New jobs per day"
                loading={dataLoading}
                error={trendsFailed || !trendRows}
                ariaLabel="Area chart of new jobs per day"
              >
                {trendRows && (
                  <ShipmentsAreaChart
                    data={trendRows.jobs.map(d => ({ date: d.date, count: d.count }))}
                    xDataKey="date"
                    series={[
                      { key: 'count', name: 'Jobs', stroke: '#22c55e', fill: '#22c55e33' },
                    ]}
                  />
                )}
              </ChartCard>
            </div>

            <ChartCard
              title="Order status mix"
              loading={dataLoading}
              error={statsFailed.orders || !o}
              ariaLabel="Donut chart of order status counts"
            >
              {o &&
                (orderStatsToDonut(o).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No orders to chart.</p>
                ) : (
                  <ConditionDonutChart data={orderStatsToDonut(o)} />
                ))}
            </ChartCard>

            <Card>
              <CardContent className="pt-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">QR codes</div>
                  {dataLoading ? (
                    <div className="h-7 w-40 animate-pulse rounded bg-muted mt-1" />
                  ) : statsFailed.qr || !stats.qr ? (
                    <div className="text-sm text-destructive">Failed to load</div>
                  ) : (
                    <div className="text-lg font-semibold">
                      {stats.qr.assigned} / {stats.qr.total} assigned ({stats.qr.assignedPercentage}%)
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="text-primary text-sm font-medium underline"
                  onClick={() => navigate('/qr-codes')}
                >
                  QR Codes
                </button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="operations" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-col gap-2 space-y-0 pb-2 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base font-heading">Jobs</CardTitle>
                <Button variant="ghost" size="sm" className="h-8 w-fit shrink-0 self-start text-primary sm:self-auto" onClick={() => navigate('/jobs')}>
                  View all jobs
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {(
                    [
                      ['Total', (x: JobStats) => x.total, Layers],
                      ['Draft', (x: JobStats) => x.draft, FileEdit],
                      ['Assigned', (x: JobStats) => x.assigned, UserCheck],
                      ['In progress', (x: JobStats) => x.in_progress, Loader2],
                      ['Completed', (x: JobStats) => x.completed, CircleCheck],
                      ['Overdue', (x: JobStats) => x.overdue, AlarmClock],
                    ] as const
                  ).map(([label, get, Icon]) => (
                    <StatTile
                      key={label}
                      label={label}
                      value={j && !statsFailed.jobs ? get(j) : '—'}
                      loading={dataLoading}
                      error={statsFailed.jobs}
                      icon={Icon}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-col gap-2 space-y-0 pb-2 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base font-heading">Tasks</CardTitle>
                <Button variant="ghost" size="sm" className="h-8 w-fit shrink-0 self-start text-primary sm:self-auto" onClick={() => navigate('/tasks')}>
                  View all tasks
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {(
                    [
                      ['Pending', (x: TaskStats) => x.pending, Hourglass],
                      ['Assigned', (x: TaskStats) => x.assigned, UserPlus],
                      ['In progress', (x: TaskStats) => x.in_progress, Loader2],
                      ['Completed', (x: TaskStats) => x.completed, CircleCheck],
                      ['Blocked', (x: TaskStats) => x.blocked, Ban],
                      ['Overdue', (x: TaskStats) => x.overdue, AlarmClock],
                    ] as const
                  ).map(([label, get, Icon]) => (
                    <StatTile
                      key={label}
                      label={label}
                      value={t && !statsFailed.tasks ? get(t) : '—'}
                      loading={dataLoading}
                      error={statsFailed.tasks}
                      icon={Icon}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-col gap-2 space-y-0 pb-2 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base font-heading">Shipments</CardTitle>
                <Button variant="ghost" size="sm" className="h-8 w-fit shrink-0 self-start text-primary sm:self-auto" onClick={() => navigate('/shipments')}>
                  View all shipments
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <StatTile
                    label="Total"
                    value={stats.shipments?.total ?? '—'}
                    loading={dataLoading}
                    error={statsFailed.shipments}
                    onClick={() => navigate('/shipments')}
                    icon={Boxes}
                  />
                  <StatTile
                    label="Active"
                    value={stats.shipments?.active ?? '—'}
                    loading={dataLoading}
                    error={statsFailed.shipments}
                    onClick={() => navigate('/shipments')}
                    icon={Radio}
                  />
                  <StatTile
                    label="Delivered"
                    value={stats.shipments?.delivered ?? '—'}
                    loading={dataLoading}
                    error={statsFailed.shipments}
                    onClick={() => navigate('/shipments')}
                    icon={PackageCheck}
                  />
                  <StatTile
                    label="Overdue"
                    value={stats.shipments?.overdue ?? '—'}
                    loading={dataLoading}
                    error={statsFailed.shipments}
                    onClick={() => navigate('/shipments')}
                    icon={AlertTriangle}
                  />
                </div>
                <div className="rounded-lg border border-border/80 bg-muted/40 px-4 py-3">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">By type</p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    {(
                      [
                        ['inbound', ArrowDownToLine],
                        ['outbound', ArrowUpFromLine],
                        ['transfer', ArrowLeftRight],
                      ] as const
                    ).map(([key, TypeIcon]) => (
                      <div key={key} className="flex flex-col items-center gap-2">
                        <TypeIcon className="h-5 w-5 text-primary/60" aria-hidden />
                        {dataLoading ? (
                          <div className="h-7 w-10 animate-pulse rounded bg-muted" />
                        ) : statsFailed.shipments || !stats.shipments ? (
                          <span className="text-sm text-destructive">—</span>
                        ) : (
                          <div className="text-xl font-bold tabular-nums">{stats.shipments.byType[key]}</div>
                        )}
                        <div className="text-xs capitalize text-muted-foreground">{key}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <ChartCard title="Jobs per day" loading={dataLoading} error={trendsFailed || !trendRows}>
                {trendRows && (
                  <ShipmentsAreaChart
                    data={trendRows.jobs.map(d => ({ date: d.date, count: d.count }))}
                    xDataKey="date"
                    series={[{ key: 'count', name: 'Jobs', stroke: '#22c55e', fill: '#22c55e33' }]}
                  />
                )}
              </ChartCard>
              <ChartCard title="Tasks per day" loading={dataLoading} error={trendsFailed || !trendRows}>
                {trendRows && (
                  <ShipmentsAreaChart
                    data={trendRows.tasks.map(d => ({ date: d.date, count: d.count }))}
                    xDataKey="date"
                    series={[{ key: 'count', name: 'Tasks', stroke: '#a855f7', fill: '#a855f733' }]}
                  />
                )}
              </ChartCard>
              <ChartCard title="Shipments per day" loading={dataLoading} error={trendsFailed || !trendRows}>
                {trendRows && (
                  <ShipmentsAreaChart
                    data={trendRows.shipments.map(d => ({ date: d.date, count: d.count }))}
                    xDataKey="date"
                    series={[{ key: 'count', name: 'Shipments', stroke: '#f59e0b', fill: '#f59e0b33' }]}
                  />
                )}
              </ChartCard>
            </div>
          </TabsContent>

          <TabsContent value="inventory" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard title="Total units" value={inst?.total ?? '—'} loading={dataLoading} error={statsFailed.assetInstances} icon={Boxes} />
              <KpiCard title="Available" value={inst?.available ?? '—'} loading={dataLoading} error={statsFailed.assetInstances} icon={PackageCheck} />
              <KpiCard title="Rented" value={inst?.rented ?? '—'} loading={dataLoading} error={statsFailed.assetInstances} icon={KeyRound} />
              <KpiCard title="Maintenance" value={inst?.maintenance ?? '—'} loading={dataLoading} error={statsFailed.assetInstances} icon={Wrench} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard
                title="Utilization"
                loading={dataLoading}
                error={statsFailed.assetInstances || inst == null}
                ariaLabel="Radial gauge of fleet utilization"
              >
                {inst != null && (
                  <div>
                    <SlaRadialGauge value={inst.utilization_rate} label="Utilization %" fill="#16a34a" />
                  </div>
                )}
              </ChartCard>
              <ChartCard
                title="Instance mix"
                loading={dataLoading}
                error={statsFailed.assetInstances || !inst}
              >
                {inst &&
                  (instanceStatusDonut(inst).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No instances to chart.</p>
                  ) : (
                    <ConditionDonutChart data={instanceStatusDonut(inst)} />
                  ))}
              </ChartCard>
            </div>

            <ChartCard
              title="New asset instances per day"
              loading={dataLoading}
              error={trendsFailed || !trendRows}
            >
              {trendRows && (
                <ShipmentsAreaChart
                  data={trendRows.asset_instances.map(d => ({ date: d.date, count: d.count }))}
                  xDataKey="date"
                  series={[{ key: 'count', name: 'Instances', stroke: '#6366f1', fill: '#6366f133' }]}
                />
              )}
            </ChartCard>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Templates</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  {dataLoading ? (
                    <div className="h-20 animate-pulse rounded bg-muted" />
                  ) : statsFailed.assetTemplates || !stats.assetTemplates ? (
                    <p className="text-destructive">Failed to load</p>
                  ) : (
                    <>
                      <div>Templates: {stats.assetTemplates.totalTemplates}</div>
                      <div>Active templates: {stats.assetTemplates.totalActiveTemplates}</div>
                      <div>Rented units: {stats.assetTemplates.totalRentedUnits}</div>
                    </>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Categories</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  {dataLoading ? (
                    <div className="h-20 animate-pulse rounded bg-muted" />
                  ) : statsFailed.assetCategories || !stats.assetCategories ? (
                    <p className="text-destructive">Failed to load</p>
                  ) : (
                    <>
                      <div>Categories: {stats.assetCategories.total}</div>
                      <div>Active: {stats.assetCategories.active}</div>
                      <div>Products: {stats.assetCategories.totalProducts}</div>
                      <div>Empty: {stats.assetCategories.empty}</div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Suppliers</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {dataLoading ? (
                  <div className="h-12 animate-pulse rounded bg-muted" />
                ) : statsFailed.suppliers || !stats.suppliers ? (
                  <p className="text-destructive">Failed to load</p>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    <span>Total {stats.suppliers.total}</span>
                    <span>Active {stats.suppliers.active}</span>
                    <span>Linked products {stats.suppliers.linkedProducts}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 flex justify-between items-center">
                <div className="text-sm">
                  {stats.qr && !statsFailed.qr ? (
                    <>
                      Scans recorded: <strong>{stats.qr.totalScans}</strong> (avg {stats.qr.avgScans} / code)
                    </>
                  ) : dataLoading ? (
                    <div className="h-4 w-48 animate-pulse rounded bg-muted" />
                  ) : (
                    <span className="text-destructive">QR stats unavailable</span>
                  )}
                </div>
                <button type="button" className="text-primary underline text-sm" onClick={() => navigate('/qr-codes')}>
                  Manage QR codes
                </button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pipeline" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-col gap-2 space-y-0 pb-2 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base font-heading">Orders</CardTitle>
                <Button variant="ghost" size="sm" className="h-8 w-fit shrink-0 self-start text-primary sm:self-auto" onClick={() => navigate('/orders')}>
                  View all orders
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {(
                    [
                      ['Pending', (x: OrderStats) => x.pending, Clock],
                      ['Confirmed', (x: OrderStats) => x.confirmed, BadgeCheck],
                      ['In progress', (x: OrderStats) => x.in_progress, Loader2],
                      ['Completed', (x: OrderStats) => x.completed, CircleCheck],
                    ] as const
                  ).map(([label, get, Icon]) => (
                    <StatTile
                      key={label}
                      label={label}
                      value={o && !statsFailed.orders ? get(o) : '—'}
                      loading={dataLoading}
                      error={statsFailed.orders}
                      onClick={() => navigate('/orders')}
                      icon={Icon}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Quote pipeline</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-2 text-sm">
                  {dataLoading ? (
                    <div className="h-32 animate-pulse rounded bg-muted col-span-2" />
                  ) : statsFailed.quotes || !stats.quotes ? (
                    <p className="text-destructive col-span-2">Failed to load</p>
                  ) : (
                    <>
                      {(['draft', 'ready', 'sent', 'viewed', 'accepted', 'rejected', 'expired', 'converted'] as const).map(
                        st => (
                          <div key={st} className="flex justify-between border-b border-border/50 py-1">
                            <span className="capitalize">{st}</span>
                            <span className="font-medium">{stats.quotes![st]}</span>
                          </div>
                        )
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Quote value</CardTitle>
                </CardHeader>
                <CardContent>
                  {dataLoading ? (
                    <div className="h-16 animate-pulse rounded bg-muted" />
                  ) : statsFailed.quotes || !stats.quotes ? (
                    <p className="text-destructive">Failed to load</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-2xl font-bold">{formatCurrency(stats.quotes.total_value)}</div>
                      <div className="text-sm text-muted-foreground">
                        Avg per accepted/converted quote: {formatCurrency(stats.quotes.avg_value)}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Orders per day" loading={dataLoading} error={trendsFailed || !trendRows}>
                {trendRows && (
                  <ShipmentsAreaChart
                    data={trendRows.orders.map(d => ({ date: d.date, count: d.count }))}
                    xDataKey="date"
                    series={[{ key: 'count', name: 'Orders', stroke: '#0ea5e9', fill: '#0ea5e933' }]}
                  />
                )}
              </ChartCard>
              <ChartCard title="Quotes per day" loading={dataLoading} error={trendsFailed || !trendRows}>
                {trendRows && (
                  <ShipmentsAreaChart
                    data={trendRows.quotes.map(d => ({ date: d.date, count: d.count }))}
                    xDataKey="date"
                    series={[{ key: 'count', name: 'Quotes', stroke: '#ec4899', fill: '#ec489933' }]}
                  />
                )}
              </ChartCard>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <footer className="mt-16 border-t bg-muted/30">
        <div className="container mx-auto px-4 py-6 text-center">
          <p className="font-body text-muted-foreground leading-relaxed">
            Homebound Asset Tracking System v1.0 - Admin Dashboard
          </p>
        </div>
      </footer>
    </div>
  );
}
